import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getStoredUser, getStoredMirror, performLogout } from '../../hooks/useAuth';
import { VACANCY_REGISTRY, type VacancyId } from '../../config/vacancies';
import { fallbackMirror } from '../../config/mirrors';
import VacancyCard from './components/VacancyCard';

export default function VacancySelectionPage() {
  const navigate = useNavigate();
  const user = getStoredUser();
  const mirror = getStoredMirror();

  useEffect(() => {
    if (!user) navigate('/', { replace: true });
  }, [user, navigate]);

  const handleLogout = () => {
    performLogout();
    navigate('/', { replace: true });
  };

  // Неизвестные фронту id отбрасываем; если не осталось ничего — фолбэк.
  let cards = mirror.vacancies
    .map((id) => VACANCY_REGISTRY[id as VacancyId])
    .filter(Boolean);

  if (cards.length === 0) {
    console.warn(`[mirrors] зеркало "${mirror.id}": нет известных вакансий, применён фолбэк`);
    cards = fallbackMirror(mirror.id).vacancies
      .map((id) => VACANCY_REGISTRY[id])
      .filter(Boolean);
  }

  const gridCols = cards.length >= 3 ? 'md:grid-cols-3' : 'md:grid-cols-2';

  return (
    <div
      className="min-h-screen bg-[#000000] flex flex-col relative overflow-hidden"
    >
      {/* Background decorative glows */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <div
          className="absolute -top-32 -left-32 w-[480px] h-[480px] rounded-full opacity-10"
          style={{ background: 'radial-gradient(circle, #5348e8 0%, transparent 70%)' }}
        />
        <div
          className="absolute bottom-0 right-0 w-[400px] h-[400px] rounded-full opacity-8"
          style={{ background: 'radial-gradient(circle, #5348e8 0%, transparent 70%)' }}
        />
        <div
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[300px] rounded-full opacity-5"
          style={{ background: 'radial-gradient(ellipse, #5348e8 0%, transparent 70%)' }}
        />
      </div>

      {/* Top bar */}
      <header className="flex items-center justify-between px-8 py-4 border-b border-[#18181c] relative z-10">
        <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-primary-500/30 to-transparent" />
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-primary-500/10 border border-primary-500/20">
            <i className="ri-shield-user-line text-xl text-primary-300" />
          </div>
          <div className="hidden sm:flex flex-col leading-tight">
            <span className="text-[11px] text-foreground-500 tracking-widest uppercase">Корпоративный</span>
            <span className="text-sm font-semibold text-white tracking-wide">Портал сотрудников</span>
          </div>
        </div>
        <div className="flex items-center gap-4">
          <span className="text-sm text-foreground-600">
            {user?.name}
          </span>
          <button
            onClick={handleLogout}
            className="flex items-center gap-2 text-sm text-foreground-500 hover:text-foreground-700 transition-colors cursor-pointer whitespace-nowrap"
          >
            <span className="w-4 h-4 flex items-center justify-center">
              <i className="ri-logout-box-r-line" />
            </span>
            Выйти
          </button>
        </div>
      </header>

      {/* Main content */}
      <main className="flex-1 flex flex-col items-center justify-center px-6 py-16 relative z-10">
        <div className="text-center mb-14">
          <span className="inline-block text-xs font-semibold text-primary-500 bg-primary-500/10 border border-primary-500/20 rounded-full px-4 py-1.5 mb-4">
            Выбор направления
          </span>
          <h1 className="text-4xl font-bold text-white mb-3">
            На какой позиции вы работаете?
          </h1>
          <p className="text-foreground-500 text-base">
            Выберите вашу роль, чтобы перейти в личный кабинет
          </p>
        </div>

        <div className={`grid grid-cols-1 ${gridCols} gap-6 w-full max-w-3xl`}>
          {cards.map((v) => (
            <VacancyCard key={v.id} vacancy={v} onSelect={(route) => navigate(route)} />
          ))}
        </div>
      </main>

      {/* Footer branding */}
      <footer className="py-5 border-t border-[#18181c] flex items-center justify-center gap-3 relative z-10">
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-primary-500/20 to-transparent" />
        <span className="text-xs text-foreground-300 tracking-widest uppercase">© 2026 Корпоративный портал</span>
        <span className="text-foreground-300">·</span>
        <span className="text-xs text-foreground-300 tracking-widest">v1.0.0</span>
      </footer>
    </div>
  );
}
